import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "../../components/select";
import Modal from "react-modal";

import styles from "./homePage.module.scss";
import PostPopup from "./postPopup";
import PostCard from "./postCard";
import GuideSectionPopup from "./guide/guidePopup";
import SliderShow from "./swiper";
import ViewPostPopup from "./viewPostPopup";
import Spinner from "../../components/spinner";
import { Search, X } from "lucide-react";
import { useLanguage } from "../../context/languageContext";
import { fetchAllGenresByLangOptions, fetchAllTagOptions, fetchAllSubPillarOptions, convertToCloudFrontUrl } from "../../helper";
import { foldForSearch } from "../../helper/searchFold";
import { apiErrorMessage, apiBodyMessage } from "../../helper/apiError";
import { useConfirm } from "../../components/confirmDialog";
import ErrorBoundary from "../../components/errorBoundary";


const HomePageContent = () => {
  const{ selectedLang, adminInfo } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  const [filters, setFilters] = useState({ postDate:'', genres: [] });
  const [filtersByPostsData, setFiltersByPostsData] = useState([]);
  const [allPostsByFilters, setAllPostsByFilters] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);

  const [allPostsData, setAllPostsData] = useState([]);
  const [editPostData, setEditPostData] = useState(null);
  const [allGenresOptions, setAllgenresOptions] = useState([]);
  const [allTagsOptions, setAllTagsOptions] = useState([]);
  const [allSubPillarOptions, setAllSubPillarOptions] = useState([]);

  const [isSwiperModalOpen, setIsSwiperModalOpen] = useState(false);
  const [isAddPostModalOpen, setIsAddPostModalOpen] = useState(false);
  const [isEditPostModalOpen, setIsEditPostModalOpen] = useState(false);

  const [isAddGuideModalOpen, setIsAddGuideModalOpen] = useState(false);
  const [isEditGuideModalOpen, setIsEditGuideModalOpen] = useState(false);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");

  const hasFetchedPosts = useRef(false);
  const confirm = useConfirm();
  // The post being deleted, so a second click on the bin can't send a second delete.
  const deletingId = useRef(null);

  useEffect(() => {
    if (adminInfo && adminInfo.token && !hasFetchedPosts.current) {
      fetchAllPostsAndReelsData();
      document.title = "Penspace - contents";
      hasFetchedPosts.current = true;
    }
  }, [adminInfo]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      // Runs on mount too, before the session may be in — `adminInfo.token`
      // on a null adminInfo threw inside the timer.
      if (adminInfo?.token && filters && filters.genres) {
        fetchAllBooksAndGenressData();
      }
    }, 300);
  
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.genres, adminInfo?.token]);

  useEffect(() => {
    // A filter that matched nothing is still a result. The old branch toasted
    // and then fell through WITHOUT swapping the data, so the page kept showing
    // every unfiltered post while claiming there were none — the filter looked
    // like it had silently failed.
    if (Array.isArray(filtersByPostsData.allPosts)) {
      setAllPostsByFilters(filtersByPostsData);
      setTotalUserCount(filtersByPostsData.totalUserCount);
      if (filtersByPostsData.allPosts.length === 0) {
        toast.info("No posts match these filters.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } else {
      setAllPostsByFilters(allPostsData);
      setTotalUserCount(allPostsData.totalUserCount);
    }
  }, [filtersByPostsData.allPosts, allPostsData]);

  // Title search runs on the client over the set already on screen — every post
  // is loaded up front, so this is instant and, crucially, it narrows the
  // SERVER-filtered results rather than replacing them. Filter then search.
  const visiblePosts = useMemo(() => {
    const source = allPostsByFilters || {};
    const query = foldForSearch(searchTitle.trim());
    if (!query) return source;

    const matches = (post) => foldForSearch(post.title).includes(query);
    return {
      ...source,
      allPosts: (source.allPosts || []).filter(matches),
      allPostsData: (source.allPostsData || []).filter(matches),
      allReelsData: (source.allReelsData || []).filter(matches),
      allGuidesData: (source.allGuidesData || []).filter(matches),
    };
  }, [allPostsByFilters, searchTitle]);

  const isSearching = searchTitle.trim().length > 0;

  // Tab-aware, not "are there any posts at all". A search that matches only a
  // guide, viewed from the Reels tab, otherwise renders an empty area with no
  // explanation — the grid thinks it has results, the tab has none.
  const activeTabPosts =
    (activeTab === 1 ? visiblePosts.allPosts
      : activeTab === 2 ? visiblePosts.allPostsData
      : activeTab === 3 ? visiblePosts.allReelsData
      : visiblePosts.allGuidesData) || [];
  const hasVisiblePosts = activeTabPosts.length > 0;

  // A filter is "applied" once a response has come back — which is exactly when
  // there's something to reset. The old test also looked at the unapplied form
  // inputs, so opening the modal and picking a date without applying swapped the
  // Filter button for Reset and there was no way back to the modal.
  const isFilterApplied = Array.isArray(filtersByPostsData.allPosts);

  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
  };

  const genreCacheRef = useRef(null);

  // Normalize post images: convert S3 URLs to CloudFront URLs
  const normalizePostImages = (post) => {
    const normalized = { ...post };
    
    // Normalize posts image
    if (normalized.posts?.uploadedImageUrl) {
      normalized.posts = {
        ...normalized.posts,
        uploadedImageUrl: convertToCloudFrontUrl(normalized.posts.uploadedImageUrl)
      };
    }
    
    // Normalize reels video
    if (normalized.reels?.uploadedImageUrl) {
      normalized.reels = {
        ...normalized.reels,
        uploadedImageUrl: convertToCloudFrontUrl(normalized.reels.uploadedImageUrl)
      };
    }
    
    // Normalize carousel images
    if (normalized.carouselImages?.length > 0) {
      normalized.carouselImages = normalized.carouselImages.map(item => ({
        ...item,
        image: item.image?.uploadedImageUrl ? {
          ...item.image,
          uploadedImageUrl: convertToCloudFrontUrl(item.image.uploadedImageUrl)
        } : item.image
      }));
    }
    
    return normalized;
  };

  const fetchAllPostsAndReelsData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/post/getall`, { headers, timeout: 30000 });

      if (response?.status) {
        // Normalize post images: convert S3 URLs to CloudFront URLs immediately after API fetch
        const normalizedData = {
          ...response.data,
          allPosts: response.data.allPosts?.map(normalizePostImages) || [],
          allPostsData: response.data.allPostsData?.map(normalizePostImages) || [],
          allReelsData: response.data.allReelsData?.map(normalizePostImages) || [],
          allGuidesData: response.data.allGuidesData?.map(normalizePostImages) || []
        };

        setAllPostsData(normalizedData);
      } else {
        toast.error(apiBodyMessage(response) || "Could not load the posts.");
      }
    } catch (error) {
      // An empty page used to be the only sign the list had failed to load.
      console.error("fetchAllPostsAndReelsData catch error::", error);
      toast.error(apiErrorMessage(error, "Could not load the posts."), { toastId: "home-posts-load" });
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  // Reloads the tag list and hands it back, so a tag created from inside a post
  // or guide form is selectable straight away — the create endpoint returns a
  // message rather than the new tag, so the fresh list is where its id comes
  // from.
  const refreshTagOptions = async () => {
    try {
      const options = await fetchAllTagOptions(adminInfo.token, { strict: true });
      setAllTagsOptions(options);
      return options;
    } catch (error) {
      console.error("refreshTagOptions catch error::", error);
      toast.error(apiErrorMessage(error, "Could not reload the tag list."));
      return allTagsOptions;
    }
  };

  // Tags and sub-pillars for the post and guide forms. Loaded on its own —
  // NOT behind the genre cache below, which used to return before reaching
  // them, so one failed load left both dropdowns empty for the session.
  // "loading" | "ready" | "failed": the forms show which, offer Retry on a
  // failure, and refuse to save until it is "ready".
  const [taxonomyState, setTaxonomyState] = useState("idle");
  const taxonomyRun = useRef(0);
  const loadTaxonomy = async () => {
    if (!adminInfo?.token) return;
    const run = ++taxonomyRun.current;
    setTaxonomyState("loading");
    try {
      const [tags, subPillars] = await Promise.all([
        fetchAllTagOptions(adminInfo.token, { strict: true }),
        fetchAllSubPillarOptions(adminInfo.token, { strict: true }),
      ]);
      if (run !== taxonomyRun.current) return;
      setAllTagsOptions(tags);
      setAllSubPillarOptions(subPillars);
      setTaxonomyState("ready");
    } catch (error) {
      if (run !== taxonomyRun.current) return;
      console.error("loadTaxonomy catch error::", error);
      setTaxonomyState("failed");
      toast.error(apiErrorMessage(error, "Could not load pillars and tags."), { toastId: "home-taxonomy-load" });
    }
  };

  const fetchAllBooksAndGenressData = async () => {
    // `?.length`, not truthiness: `fetchAllGenresByLangOptions` returns [] on
    // any error, and an empty array is truthy. Caching that meant one failed
    // request — a transient 401, a blip — left the genre dropdown empty for the
    // rest of the session with no way to recover but a reload. It also skipped
    // the tag and sub-pillar fetches below, which is why those kept working
    // while genres did not.
    if (genreCacheRef.current?.length) {
      setAllgenresOptions(genreCacheRef.current);
      return;
    }
  
    try {
      if (!adminInfo?.token) return;
      // The helper answers [] on any failure — there are always genres, so an
      // empty list means the load failed.
      const genresOptions = await fetchAllGenresByLangOptions(selectedLang, adminInfo.token);
      const options = (genresOptions || []).map((genre) => ({
        label: genre.genreName,
        value: genre.genreId,
        type: genre.type,
      }));
      if (options.length === 0) {
        toast.error("Could not load the genres for the filter.", { toastId: "home-genres-load" });
        return;
      }
      genreCacheRef.current = options;
      setAllgenresOptions(options);
    } catch (error) {
      console.error("fetchAllBooksAndGenressData catch error::", error);
      toast.error(apiErrorMessage(error, "Could not load the genres for the filter."), { toastId: "home-genres-load" });
    }
  };

  const handleChangeAddPostAndReelsModal = () => {
    setIsAddPostModalOpen(true);
    setIsEditPostModalOpen(false);
    setEditPostData(null);
    loadTaxonomy();
    fetchAllBooksAndGenressData();
  };
  
  const handleChangeAddGuideModal = () => {
    setIsAddGuideModalOpen(true);
    setIsEditGuideModalOpen(false);
    setEditPostData(null);
    loadTaxonomy();
    fetchAllBooksAndGenressData();
  };

  const handleCloseGuideModal = (isVisible) => {
    setIsAddGuideModalOpen(isVisible);
    setIsEditGuideModalOpen(isVisible);
  };

  const handleClosePostModal = (isVisible) => {
    setIsAddPostModalOpen(isVisible);
    setIsEditPostModalOpen(isVisible);
  };

  const handleChangeEditPostAndReelsModal = (post) => {
    setEditPostData(post);
    setIsEditPostModalOpen(true);
    setIsAddPostModalOpen(false);
    setIsSwiperModalOpen(false);
    loadTaxonomy();
    fetchAllBooksAndGenressData();
  };

  const handleChangeEditGuideModal = (post) => {
    setEditPostData(post);
    setIsAddGuideModalOpen(true);
    setIsEditGuideModalOpen(true);
    loadTaxonomy();
    fetchAllBooksAndGenressData();
  };

  const handleChangeShowSwiperModal = (post) => {
    setEditPostData(post);
  
    if (activeTab === 4) {
      setIsSwiperModalOpen(true);
      setIsEditGuideModalOpen(false);
    } else {
      setIsSwiperModalOpen(false);
      setIsEditGuideModalOpen(true);
    }
  };

  // Clicking a card opens a read-only detail view. The Guides tab keeps its own
  // carousel — it is already a viewer, and pages read better full-width there.
  const [viewPost, setViewPost] = useState(null);

  const handleResetFilter = () => {
    setFiltersByPostsData([]);
    // The form inputs have to go too — leaving a stale date behind meant the
    // next "Apply" silently re-ran the filter you thought you'd just cleared.
    setFilters({ postDate: '', genres: [] });
    setIsFilterModalOpen(false);
  };

  // Drops one post from the lists on screen — the full list AND a filtered
  // one, which otherwise kept showing a deleted post until Reset.
  const removeFromLists = (postId) => {
    const without = (data) => {
      if (!data || !Array.isArray(data.allPosts)) return data;
      const keep = (list) => (list || []).filter((p) => p._id !== postId);
      return { ...data, allPosts: keep(data.allPosts), allPostsData: keep(data.allPostsData), allReelsData: keep(data.allReelsData), allGuidesData: keep(data.allGuidesData) };
    };
    setAllPostsData(without);
    setFiltersByPostsData(without);
  };

  // The visibility a card's switch was set to, written into the lists so the
  // detail view and a later filter agree with the switch.
  const setVisibleInLists = (postId, isVisible) => {
    const patch = (data) => {
      if (!data || !Array.isArray(data.allPosts)) return data;
      const set = (list) => (list || []).map((p) => (p._id === postId ? { ...p, isPostVisible: isVisible } : p));
      return { ...data, allPosts: set(data.allPosts), allPostsData: set(data.allPostsData), allReelsData: set(data.allReelsData), allGuidesData: set(data.allGuidesData) };
    };
    setAllPostsData(patch);
    setFiltersByPostsData(patch);
  };

  const handleRemoveHomePageConents = async (postId) => {
    if (!postId || deletingId.current) return;

    try {
      // One click on the bin used to delete straight away.
      const post = (allPostsData.allPosts || []).find((p) => p._id === postId);
      const ok = await confirm({
        title: "Delete this post?",
        quote: post?.title || undefined,
        body: "It moves to the trash and leaves the app.",
        confirmLabel: "Delete",
      });
      if (!ok) return;
      deletingId.current = postId;
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      // `headers` was passed where axios.put expects the body, so the config
      // slot was empty and no Authorization header was ever sent — every
      // delete came back 401, and with nothing catching it the page fell over
      // instead of saying so.
      const { data: response } = await axios.put(
        `${process.env.REACT_APP_API_ROOT}/api/admin/post/delete/${postId}`,
        {},
        { headers }
      );

      // `response.status` on the axios object is the HTTP code, which is
      // truthy on any 2xx. The API's own flag is what says whether the delete
      // actually happened.
      if (response?.status) {
        removeFromLists(postId);
        toast.success(response.message || "Post moved to the trash.");
      } else {
        toast.error(apiBodyMessage(response) || "Could not delete this.");
      }
    } catch (error) {
      console.error("handleRemoveHomePageConents catch error::", error);
      toast.error(apiErrorMessage(error, "Could not delete this."));
    } finally {
      deletingId.current = null;
    }
  };

  // Answers whether the change was saved: the card flips its switch at once
  // and flips it back on `false` — before, a failed save left the switch
  // showing a state the post wasn't in, with no message.
  const handleChangeActiveStatus = async (postId, isPostActive) => {
    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/post/update/status/${postId}`, { isActive: isPostActive }, { headers, timeout: 30000 });

      if (response?.status) {
        setVisibleInLists(postId, isPostActive);
        toast.success(isPostActive ? "Post status has been activated." : "Post status has been deactivated.");
        return true;
      }
      toast.error(apiBodyMessage(response) || "The visibility was not changed.");
      return false;
    } catch (error) {
      console.error("handleChangeActiveStatus catch error", error);
      toast.error(apiErrorMessage(error, "The visibility was not changed."));
      return false;
    }
  };

  const handleSelectFilterValues = async (field, value) => {
    setFilters((prevValues) => ({
      ...prevValues,
        [field]: value,
    }));
  };
  
  const handleSearchFilterValues = async () => {
    if (!filters.postDate && !(filters.genres && filters.genres.length > 0)) {
      toast.info("Pick a date or a genre to filter by.");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/post-by-filters`, { filters }, { headers, timeout: 30000 });

      if (response?.status) {
        // Normalize filtered post images: convert S3 URLs to CloudFront URLs
        const normalizedFilteredData = {
          ...response.data,
          allPosts: response.data.allPosts?.map(normalizePostImages) || [],
          allPostsData: response.data.allPostsData?.map(normalizePostImages) || [],
          allReelsData: response.data.allReelsData?.map(normalizePostImages) || [],
          allGuidesData: response.data.allGuidesData?.map(normalizePostImages) || []
        };

        setFiltersByPostsData(normalizedFilteredData);
        setFilters({ postDate:'', genres: [] });
        setIsFilterModalOpen(false);
      } else {
        toast.error(apiBodyMessage(response) || "Could not apply the filter.");
      }
    } catch (error) {
      console.error("handleSearchFilterValues catch error", error);
      toast.error(apiErrorMessage(error, "Could not apply the filter."));
    }
  };

  const today = new Date();
  const todayDateString = today.toISOString().split('T')[0];

  const filterPopupStyles = {
    position: "fixed",
    top: '80px',
    right: '20px',
    bottom: 'auto',
    left: 'auto',
    width: '320px',
    padding: '20px',
    backgroundColor: 'var(--ps-surface)',
    color: 'var(--ps-text)',
    borderRadius: '12px',
    border: '1px solid var(--ps-border)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    zIndex: 1050,
    overflow: 'auto',
    height: '80vh',
  };


  return (
    <React.Fragment>
      {loading ? (
        <Spinner />
      ) : (
        <React.Fragment>
          <div className={styles.wrapper}>
            <div className="heading">
              <h4>Home Page</h4>
              <div className="d-flex align-items-center gap-2">
                <div className={styles.searchBox}>
                  <Search size={16} className={styles.searchIcon} aria-hidden />
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search by title..."
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                    aria-label="Search posts by title"
                  />
                  {isSearching && (
                    <X
                      size={16}
                      className={styles.searchClear}
                      role="button"
                      aria-label="Clear search"
                      onClick={() => setSearchTitle("")}
                    />
                  )}
                </div>
                {(allPostsData && allPostsData.allPosts && allPostsData.allPosts.length > 0) && (
                isFilterApplied ? (
                  <button type="button" className="btn btn-dark" onClick={handleResetFilter}>Reset filter</button>
                ) : (
                  <div className={styles.filter} onClick={() => setIsFilterModalOpen(true)} >
                    <h6 className="text-light fw-bold ps-1 mt-1">Filter</h6>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white" className="bi bi-list" viewBox="0 0 16 16" >
                      <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.tabs}>
              <div className="d-flex">
                <p className={activeTab === 1 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(1)}>
                  All{" "}{visiblePosts.allPosts && visiblePosts.allPosts.length > 0 && `(${visiblePosts.allPosts.length})`}
                </p>
                <p className={activeTab === 2 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(2)}>
                  Posts{" "}{visiblePosts.allPostsData && visiblePosts.allPostsData.length > 0 && `(${visiblePosts.allPostsData.length})`}
                </p>
                <p className={activeTab === 3 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(3)}>
                  Reels{" "}{visiblePosts.allReelsData && visiblePosts.allReelsData.length > 0 && `(${visiblePosts.allReelsData.length})`}
                </p>
                <p className={activeTab === 4 ? styles.activeTab : styles.tab} onClick={() => handleSetActiveTab(4)}>
                  Guides{" "}{visiblePosts.allGuidesData && visiblePosts.allGuidesData.length > 0 && `(${visiblePosts.allGuidesData.length})`}
                </p>
              </div>
              <div className={`d-flex gap-1 justify-content-end ${styles.buttons}`}>
                {(activeTab === 2 || activeTab === 3) && (
                  <button type="button" className={styles.addButton} onClick={handleChangeAddPostAndReelsModal}>
                    {activeTab === 2 ? "Add Post" : activeTab === 3 && "Add Reel"}
                  </button>
                )}
                {activeTab === 4 && (
                  <button type="button" className={styles.addButton} onClick={handleChangeAddGuideModal}>Add Guide</button>
                )}
              </div>
            </div>

            {hasVisiblePosts && (
                <ErrorBoundary name="The post list" onReset={fetchAllPostsAndReelsData}>
                <div className={styles.genreContainer}>
                  <div className={styles.masonry}>
                    {activeTab === 1 && visiblePosts.allPosts && visiblePosts.allPosts.length > 0 &&
                      visiblePosts.allPosts.map((post, index) => (
                        <div key={post._id || index} className={styles.masonryItem}>
                          <PostCard
                            post={post}
                            onClick={setViewPost}
                            onEditSuccess={handleChangeEditPostAndReelsModal}
                            onDeleteSuccess={handleRemoveHomePageConents}
                            onStatusSuccess={(postId, isActive) => handleChangeActiveStatus(postId, isActive)}
                          />
                        </div>
                      ))}
                  </div>
                  <div className={styles.masonry}>
                    {activeTab === 2 && visiblePosts.allPostsData && visiblePosts.allPostsData.length > 0 &&
                      visiblePosts.allPostsData.map((post, index) => (
                        <div key={post._id || index} className={styles.masonryItem}>
                          <PostCard
                            post={post}
                            onClick={setViewPost}
                            onEditSuccess={handleChangeEditPostAndReelsModal}
                            onDeleteSuccess={handleRemoveHomePageConents}
                            onStatusSuccess={(postId, isActive) => handleChangeActiveStatus(postId, isActive)}
                          />
                        </div>
                      ))}
                  </div>
                  <div className={styles.masonry}>
                    {activeTab === 3 && visiblePosts.allReelsData && visiblePosts.allReelsData.length > 0 &&
                      visiblePosts.allReelsData.map((post, index) => (
                        <div key={post._id || index} className={styles.masonryItem}>
                          <PostCard
                            post={post}
                            onClick={setViewPost}
                            onEditSuccess={handleChangeEditPostAndReelsModal}
                            onDeleteSuccess={handleRemoveHomePageConents}
                            onStatusSuccess={(postId, isActive) => handleChangeActiveStatus(postId, isActive)}
                            totalUserCount={totalUserCount}
                          />
                        </div>
                      ))}
                  </div>
                  <div className={styles.masonry}>
                    {activeTab === 4 && visiblePosts.allGuidesData && visiblePosts.allGuidesData.length > 0 &&
                      visiblePosts.allGuidesData.map((post, index) => (
                        <div key={post._id || index} className={styles.masonryItem}>
                          <PostCard
                            post={post}
                            onEditSuccess={handleChangeEditGuideModal}
                            onDeleteSuccess={handleRemoveHomePageConents}
                            onStatusSuccess={(postId, isActive) => handleChangeActiveStatus(postId, isActive)}
                            onClick={() => handleChangeShowSwiperModal(post)}
                          />
                        </div>
                      ))}
                  </div>
                </div>
                </ErrorBoundary>
              )}

            {/* Without this, an empty result renders a blank page that reads as
                "the posts are gone" rather than "nothing matched". */}
            {!hasVisiblePosts && (isSearching || isFilterApplied) && (
              <div className={styles.noResults}>
                <p className="mb-2">
                  {isSearching
                    ? `No posts match “${searchTitle.trim()}”.`
                    : "No posts match these filters."}
                </p>
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => (isSearching ? setSearchTitle("") : handleResetFilter())}
                >
                  {isSearching ? "Clear search" : "Reset filter"}
                </button>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {/* Each dialog guarded on its own: an error drawing one closes nothing
          else and says so. */}
      <ErrorBoundary name="The post form" onReset={() => handleClosePostModal(false)}>
      <PostPopup
        isAddPostModalOpen={isAddPostModalOpen}
        isEditPostModalOpen={isEditPostModalOpen}
        handleCloseModal={(isVisible) => handleClosePostModal(isVisible)}
        postData={editPostData}
        adminInfo={adminInfo}
        tagOptions={allTagsOptions}
        onTagCreated={refreshTagOptions}
        subPillarOptions={allSubPillarOptions}
        taxonomyState={taxonomyState}
        onRetryTaxonomy={loadTaxonomy}
        onSuccess={fetchAllPostsAndReelsData}
      />
      </ErrorBoundary>

      <ErrorBoundary name="The guide form" onReset={() => handleCloseGuideModal(false)}>
      <GuideSectionPopup
        isAddGuideModalOpen={isAddGuideModalOpen}
        isEditGuideModalOpen={isEditGuideModalOpen}
        handleCloseModal={(isVisible) => handleCloseGuideModal(isVisible)}
        postData={editPostData}
        adminInfo={adminInfo}
        subPillarOptions={allSubPillarOptions}
        tagOptions={allTagsOptions}
        onTagCreated={refreshTagOptions}
        taxonomyState={taxonomyState}
        onRetryTaxonomy={loadTaxonomy}
        onSuccess={fetchAllPostsAndReelsData}
      />
      </ErrorBoundary>

      <ErrorBoundary name="The post details" onReset={() => setViewPost(null)}>
        <ViewPostPopup
          isOpen={Boolean(viewPost)}
          post={viewPost}
          onClose={() => setViewPost(null)}
        />
      </ErrorBoundary>

      <ErrorBoundary name="The guide viewer" onReset={() => setIsSwiperModalOpen(false)}>
        <SliderShow
          isSwiperModalOpen={isSwiperModalOpen}
          postData={editPostData}
          handleCloseModal={() => setIsSwiperModalOpen(false)}
        />
      </ErrorBoundary>

      <Modal
        isOpen={isFilterModalOpen}
        onRequestClose={() => setIsFilterModalOpen(false)}
        style={{ content: filterPopupStyles }}
      >
        <div className="d-flex flex-column p-1 h-100">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold">Filter posts</h5>

            <svg className="cursor-pointer" width="14" height="14" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={() => setIsFilterModalOpen(false)}>
              <path d="M1 1L21 21M1 21L21 1L1 21Z" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div className="flex-grow-1 pe-1">
            <div className="">
              {/* The query is `createdAt >= start of this day`, so the old
                  "Select Date" label promised a single-day filter it never did. */}
              <label className="fw-medium small mb-1">Posted on or after:</label>
              <input
                className={`form-control form-control-sm px-3 w-100`}
                type="date"
                value={filters.postDate}
                max={todayDateString}
                placeholder="Enter date..."
                onKeyDown={(e) => { 
                  if (e.key === "Enter") {
                    setFilters((prevValues) => ({ ...prevValues, postDate: e.target.value }));
                  }
                }}
                onChange={(e) => setFilters((prevValues) => ({ ...prevValues, postDate: e.target.value }))}
                autoFocus
                style={{ height: "40px" }}
              />
            </div>

            <div className="mt-3">
              <label className="fw-medium small mb-1">Select Genres:</label>
              <Select
                name="genres"
                value={filters.genres?.map((item) => ({ label: item.genreName, value: item.genreId })) || []}
                onChange={(selectedOptions) => {
                    handleSelectFilterValues("genres", selectedOptions.map((option) => ({
                      genreName: option.label,
                      genreId: option.value,
                    })));
                  }}
                options={allGenresOptions.filter((option) => option.type)}
                isMulti
                styles={{
                  control: (base) => ({
                    ...base,
                    width: "270px",
                  }),
                }}
              />
            </div>
          </div>
          <div className="pt-3 d-flex justify-content-between border-top mt-3">
            <button type="button" className="btn btn-outline-danger" onClick={handleResetFilter}>Reset filter</button>
            <button type="button" className="btn btn-dark" onClick={handleSearchFilterValues}>Apply filter</button>
          </div>
        </div>
      </Modal>

    </React.Fragment>
  );
};

export default HomePageContent;
